import React from "react";
import ReactDOM from "react-dom/client";
import {createBrowserRouter, Navigate, RouterProvider} from "react-router-dom";
import "./index.css";
import App from "./App";
import English from "./lang/en.json";
import German from "./lang/de.json";
import appSettingsLocal from "./appsettings.LOCAL.json";
import {IntlProvider} from "react-intl";
import KeycloakProvider from "./views/contextProviders/KeycloakProvider";
import {DataMarketStyling} from "@spg/component_library/dm";
import HomeContainer from "./views/pages/home/HomeContainer";
import Imprint from "./views/unprotectedPages/Imprint";
import AppDetailsContainer from "./views/pages/appDetails/AppDetailsContainer";
import InstallationParameters from "./views/pages/appInstallationParameters/InstallationParameters";
import {SnackbarProvider} from "@spg/component_library";
import {EnrichedSnackbarProvider} from "./utility/SnackbarProvider";
import {AppSettings} from "./logic/APITypes";
import AppSettingsProvider from "./views/contextProviders/AppSettingsProvider";
import AuthErrorPage from "./views/unprotectedPages/AuthErrorPage";
import GroupOverview from "./views/pages/groups/Overview";
import GroupCreation from "./views/pages/groups/Creation";
import GroupDetails from "./views/pages/groups/Details";

const locale = navigator.language;
let lang = English as Record<string, string>;
if (locale.split("-")[0].toLowerCase() === "de") {
  lang = German as Record<string, string>;
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

const onSettingsLoaded = (appSettings: AppSettings) => {
  const router = createBrowserRouter([
    {
      path: "/",
      element: <App />,
      children: [
        {
          path: "/",
          element: <HomeContainer />,
        },
        {
          path: "/apps/core/:appId",
          element: <AppDetailsContainer appType="core" />,
        },
        {
          path: "/groups/:groupId/apps/installed/:appId",
          element: <AppDetailsContainer appType="installed" />,
        },
        {
          path: "/groups/:groupId/apps/available/:appId",
          element: <AppDetailsContainer appType="available" />,
        },
        {
          path: "/groups/:groupId/apps/available/:appId/parameters",
          element: <InstallationParameters />,
        },
        {
          path: "/groups",
          element: <GroupOverview />,
        },
        {
          path: "/groups/new",
          element: <GroupCreation />,
        },
        {
          path: "/groups/:groupId",
          element: <GroupDetails />,
        },
      ],
    },
    {
      path: "/imprint",
      element: <Imprint />,
    },
    {
      path: "*",
      element: <Navigate to="/" />,
    },
  ]);

  root.render(
    <AppSettingsProvider appSettings={appSettings}>
      <KeycloakProvider>
        <React.StrictMode>
          <IntlProvider locale={locale} messages={lang}>
            <SnackbarProvider>
              <EnrichedSnackbarProvider>
                <RouterProvider router={router} />
              </EnrichedSnackbarProvider>
            </SnackbarProvider>
            <DataMarketStyling />
          </IntlProvider>
        </React.StrictMode>
      </KeycloakProvider>
    </AppSettingsProvider>,
  );
};

const onSettingsError = () => {
  root.render(
    <IntlProvider locale={locale} messages={lang}>
      <DataMarketStyling />
      <AuthErrorPage reason={"noAppSettings"} afterTimeout={() => null} />
    </IntlProvider>,
  );
};

if (process.env.NODE_ENV === "development") onSettingsLoaded(appSettingsLocal);
else
  fetch("/appsettings.json").then(
    (res) =>
      res
        .json()
        .then((settings) => onSettingsLoaded(settings), onSettingsError),
    onSettingsError,
  );
